import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, writeBatch, doc } from 'firebase/firestore';
import DriverCalendar from './DriverCalendar';

const ManagerAvailability = ({ db, usersList, currentDate, logAction, initials }) => {
    const [selectedDriver, setSelectedDriver] = useState('');
    const [driverAvailability, setDriverAvailability] = useState({});
    const [originalDriverAvailability, setOriginalDriverAvailability] = useState({});
    const [saveMessage, setSaveMessage] = useState('');
    
    // Internal state for the popup (independent of the main App popup)
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    // --- FETCH: Get full year for the selected driver ---
    const fetchDriverAvailability = async (driverId) => {
        if (!driverId) return;
        const year = currentDate.getFullYear();
        const q = query(collection(db, "availability"), where("initials", "==", driverId), where("year", "==", year));
        const s = await getDocs(q);
        const data = {};
        s.forEach(doc => {
            const r = doc.data();
            if (!data[r.month]) data[r.month] = {};
            data[r.month][r.day] = { status: r.status, timeNote: r.timeNote || "", managerDecision: r.managerDecision };
        });
        setDriverAvailability(data);
        setOriginalDriverAvailability(JSON.parse(JSON.stringify(data)));
        setSaveMessage('');
    };

    useEffect(() => {
        if (selectedDriver) fetchDriverAvailability(selectedDriver);
        else setDriverAvailability({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDriver, currentDate]);

    // --- HANDLERS ---
    const handleDateClick = (month, day, currentData) => {
        const date = new Date(currentDate.getFullYear(), month, day);
        // Only allow weekends (Saturday=6, Sunday=0)
        if(date.getDay() !== 6 && date.getDay() !== 0) return;
        
        setSelectedDate({ 
            month, 
            day, 
            year: currentDate.getFullYear(), 
            data: currentData || {} 
        });
        setIsPopupOpen(true);
    };

    // Updates the local state (visual only) until saved
    const handleUpdateLocal = (field, value) => {
        if (!selectedDate) return;
        
        // 1. Update the big availability object
        setDriverAvailability(prev => ({
            ...prev,
            [selectedDate.month]: {
                ...prev[selectedDate.month],
                [selectedDate.day]: {
                    ...(prev[selectedDate.month]?.[selectedDate.day] || {}),
                    [field]: value
                }
            }
        }));

        // 2. Update the currently open popup data
        setSelectedDate(prev => ({...prev, data: {...prev.data, [field]: value}}));
    };

    const handleSaveChanges = async () => {
        if (!selectedDriver) return;
        setSaveMessage('Saving...');
        const batch = writeBatch(db);
        const year = currentDate.getFullYear();
        let changesDescription = [];

        Object.keys(driverAvailability).forEach(m => {
            const monthData = driverAvailability[m];
            Object.keys(monthData).forEach(d => {
                const dayData = monthData[d];
                const oldData = originalDriverAvailability[m]?.[d] || {};
                
                // Only save if changed
                if (dayData.status !== oldData.status || dayData.timeNote !== oldData.timeNote) {
                    const docId = `${selectedDriver}_${year}_${m}_${d}`;
                    
                    // Find driver name for the record
                    const driverUser = usersList.find(u => u.initials === selectedDriver);
                    const displayName = driverUser ? driverUser.displayName : selectedDriver;

                    batch.set(doc(db, "availability", docId), {
                        initials: selectedDriver,
                        displayName: displayName,
                        year: Number(year),
                        month: Number(m),
                        day: Number(d),
                        status: dayData.status || null,
                        timeNote: dayData.timeNote || "",
                        updatedAt: new Date()
                    }, { merge: true });
                    
                    changesDescription.push(`${monthNames[m]} ${d}: ${dayData.status}`);
                }
            });
        });

        await batch.commit();
        setOriginalDriverAvailability(JSON.parse(JSON.stringify(driverAvailability)));
        setSaveMessage('✅ Saved!');
        
        if (changesDescription.length > 0 && logAction) {
            await logAction(`Manager Edit: ${selectedDriver}`, initials, `Set: ${changesDescription.join(', ')}`);
        }
        
        setTimeout(() => setSaveMessage(''), 3000);
    };

    const handleSaveAndClosePopup = async () => {
        await handleSaveChanges();
        setIsPopupOpen(false);
    };

    // Helper to determine button style
    const getStatusColor = (status) => {
        switch(status) {
            case 'Available': return '#22c55e';
            case 'Want to Work': return '#3b82f6';
            case 'Holiday': return '#facc15';
            default: return '#94a3b8'; // Unavailable
        }
    };

    const getStatusTextColor = (status) => {
        if (status === 'Holiday') return 'black';
        return 'white';
    };

    return (
        <div className="animate-fade-in">
            {/* Driver Selector */}
            <div className="card" style={{maxWidth:'500px', margin:'0 auto 20px auto'}}>
                <label style={{fontWeight:'bold', display:'block', marginBottom:'8px'}}>Select Driver to Manage:</label>
                <select onChange={e=>setSelectedDriver(e.target.value)} value={selectedDriver}>
                    <option value="">-- Select Driver --</option>
                    {usersList.map(u=><option key={u.initials} value={u.initials}>{u.displayName}</option>)}
                </select>
            </div>

            {/* The Calendar View (Only shows if driver selected) */}
            {selectedDriver && (
                <DriverCalendar 
                    currentDate={currentDate}
                    yearlyAvailability={driverAvailability}
                    saveMessage={saveMessage}
                    handleSaveChanges={handleSaveChanges}
                    handleDateClick={handleDateClick}
                />
            )}

            {/* Popup Modal (Manager Version) */}
            {isPopupOpen && selectedDate && (
                <div className="popup-overlay">
                    <div className="card animate-fade-in" style={{width:'90%', maxWidth:'400px', padding:'20px'}}>
                        <div className="flex-between" style={{marginBottom:'15px', borderBottom:'1px solid var(--border)', paddingBottom:'10px'}}>
                            <h3 style={{margin:0}}>{selectedDate.day} {monthNames[selectedDate.month]} {selectedDate.year}</h3>
                            <button onClick={()=>setIsPopupOpen(false)} style={{background:'transparent', fontSize:'18px', color:'var(--text-main)', padding:0}}>✕</button>
                        </div>
                        
                        {/* Manager Decision Indicator */}
                        {selectedDate.data.managerDecision && (
                            <div style={{marginBottom:'20px', textAlign:'center', padding:'10px', background: selectedDate.data.managerDecision==='Working'?'rgba(27,143,90,0.18)':'rgba(179,38,30,0.18)', borderRadius:'8px', border: `1px solid ${selectedDate.data.managerDecision==='Working'?'rgba(74,222,128,0.45)':'rgba(248,113,113,0.45)'}`}}>
                                <span style={{fontWeight:'bold', color: selectedDate.data.managerDecision==='Working'?'#4ade80':'#fca5a5'}}>
                                    ROTA: {selectedDate.data.managerDecision.toUpperCase()}
                                </span>
                            </div>
                        )}

                        {/* Status Buttons */}
                        <label style={{fontSize:'12px', fontWeight:'bold', marginBottom:'10px', display:'block'}}>Set Availability:</label>
                        <div style={{display:'grid', gap:'10px', marginBottom:'20px'}}>
                             {['Want to Work', 'Available', 'Unavailable', 'Holiday'].map(status => {
                                 const color = getStatusColor(status);
                                 const isSelected = selectedDate.data.status === status;
                                 return (
                                     <button 
                                        key={status}
                                        onClick={() => handleUpdateLocal('status', status)}
                                        style={{
                                            padding: '12px', fontWeight: 'bold',
                                            background: isSelected ? color : 'rgba(255,255,255,0.04)',
                                            color: isSelected ? getStatusTextColor(status) : color,
                                            border: `2px solid ${color}`
                                        }}
                                     >
                                         {status}
                                     </button>
                                 )
                             })}
                        </div>

                        <label style={{fontSize:'12px', fontWeight:'bold', marginBottom:'5px', display:'block'}}>Notes:</label>
                        <textarea 
                            value={selectedDate.data.timeNote || ''} 
                            onChange={(e) => handleUpdateLocal('timeNote', e.target.value)}
                            placeholder="Add notes on behalf of driver..."
                            style={{width:'100%', height:'60px', padding:'10px', borderRadius:'8px', border:'1px solid var(--border)', marginBottom:'20px'}}
                        />

                        <div style={{display:'grid', gap:'10px'}}>
                            <button onClick={handleSaveAndClosePopup} style={{width:'100%', background:'var(--success)', color:'white', padding:'12px'}}>Save changes</button>
                            <button onClick={()=>setIsPopupOpen(false)} style={{width:'100%', background:'var(--dark)', color:'white', padding:'12px'}}>Done</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManagerAvailability;